
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import {
  CurrentlyEditingItem,
  ElementStudioExpectedDataToLoad,
  ElementStudioSaveLocation,
  ElementStudioSaveLocationWithName,
  SaveLocationType
} from "@/types/element-studio";
import { configStore, uiStore } from "@/store/store";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import Icon from "@/components/UI/Icon.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { isBreakdanceDevelopmentEnvironment } from "@/lib/dev/development-environment";
import {
  removeChangesNotSavedWarning,
  warnIfExitingWithUnsavedChanges
} from "@/util/warn-unsaved-changes";
import ElementStudioSaveNewDialog from "@/components/ElementStudio/ElementStudioSaveNewDialog.vue";

@Component({
  components: { ElementStudioSaveNewDialog, NiceButton, Icon, Dropdown }
})
export default class ElementStudioTopBar extends Vue {
  @Prop()
  value!: CurrentlyEditingItem | false;

  @Prop()
  saveLocations!: ElementStudioExpectedDataToLoad["saveLocations"];

  @Prop()
  hasUnsavedChanges!: boolean;

  @Prop()
  itemType!: SaveLocationType;

  @Prop()
  itemName!: string;

  @Prop()
  saveInProgress!: boolean;

  @Prop()
  itemsSlug!: string[];

  deleteDialog = false;
  availableItemsSearch = "";
  closeItemOrPanelDialog: false | "panel" | "item" = false;
  showNewItemDialog = false;

  warnIfUnsavedFunction!: (e: Event) => void;

  mounted() {
    document.addEventListener(
      "breakdanceSaveButtonClicked",
      this.mainAppSaveButtonClicked
    );

    this.warnIfUnsavedFunction = warnIfExitingWithUnsavedChanges(
      () => this.hasUnsavedChanges
    );
  }

  destroyed() {
    document.removeEventListener(
      "breakdanceSaveButtonClicked",
      this.mainAppSaveButtonClicked
    );
  }

  beforeDestroy() {
    removeChangesNotSavedWarning(this.warnIfUnsavedFunction);
  }

  handleNewItem() {
    this.showNewItemDialog = true;
  }

  get availableSaveLocations() {
    return this.saveLocations.filter(saveLocation =>
      this.showSaveLocation(saveLocation)
    );
  }

  get availableItemsBySearch() {
    return this.itemsSlug.filter(slug =>
      slug
        .toLowerCase()
        .includes(this.availableItemsSearch.toLowerCase().replace(/ /g, ""))
    );
  }

  get canDeleteElement() {
    if (!this.value) return false;

    return this.availableSaveLocations.find(
      saveLocation =>
        this.value &&
        this.value.directoryPath &&
        this.value.directoryPath.startsWith(saveLocation.directoryPath)
    );
  }

  discardChanges() {
    if (this.closeItemOrPanelDialog === "panel") {
      uiStore.toggleElementStudio();
    }

    this.closeItemOrPanelDialog = false;

    this.$emit("discardChanges");
  }

  mainAppSaveButtonClicked() {
    if (this.hasUnsavedChanges) {
      if (confirm(`Also save your Element Studio ${this.itemType}?`)) {
        this.saveOverwrite(false);
      }
    }
  }

  openItem(itemSlug: string) {
    this.availableItemsSearch = "";
    this.$emit("open", itemSlug);
  }

  closeItem() {
    if (this.hasUnsavedChanges) {
      this.closeItemOrPanelDialog = "item";
    } else {
      this.$emit("reset");
    }
  }

  closePanel() {
    if (this.hasUnsavedChanges) {
      this.closeItemOrPanelDialog = "panel";
    } else {
      uiStore.toggleElementStudio();
    }
  }

  saveNewItem(newItem: ElementStudioSaveLocationWithName) {
    this.$emit("saveNew", newItem);
  }

  saveAs(saveLocation: ElementStudioSaveLocation) {
    this.$emit("saveAs", saveLocation);
  }

  saveOverwrite(bypassPsalm: boolean) {
    if (
      !this.value ||
      !this.value.directoryPath ||
      !this.canSaveOrIsAdvancedUser
    ) {
      return;
    }

    this.$emit("saveOverride", bypassPsalm);
  }

  showSaveLocation(saveLocation: ElementStudioSaveLocation) {
    if (saveLocation.type !== this.itemType) return false;

    if (!saveLocation.onlyForAdvancedUsers) return true;

    return this.showAdvancedFeatures;
  }

  get showAdvancedFeatures() {
    return (
      isBreakdanceDevelopmentEnvironment() ||
      configStore.isElementStudioPowerUser
    );
  }

  get canSaveOrIsAdvancedUser() {
    return (
      this.value &&
      (!this.value.onlyForAdvancedUsers || this.showAdvancedFeatures)
    );
  }

  deleteItem() {
    this.deleteDialog = false;
    this.$emit("delete");
  }

  switchMode() {
    this.$emit("switchMode");
  }
}
